import { Box, CloseButton, Flex, Grid, Text } from "@chakra-ui/react";
import { Navbar } from "@/components/Navbar";
import { flatRoutesList, RoutesList } from "@/Routes";
import { getInitials, isExactFeatureToggle, isExactRole } from "@/utils";
import { useNavigate } from "react-router-dom";
import AlertTerms from "@/components/AlertTerms";
import { useCallback, useEffect, useState } from "react";
import { useTerms } from "@/hooks/useTerms";
import { signOut } from "@/contexts";
import { useFeatureToggle } from "@/hooks/useFeatureToggle";
import { FiXCircle } from "react-icons/fi";
import { Sidebar, NotificationDrawer } from "@/layouts";
import { Header, DrawerList, ActionDrawerItems, Icon, Tooltip, Logo } from "barte-design-system";
import { QuestionIcon, SignOutIcon, GearSixIcon } from "@phosphor-icons/react";
import { getTenantConfig } from "@/config/tenantConfig";

declare global {
  interface Window {
    __isFirstLoad: boolean;
  }
}

function endImpersonation() {
  localStorage.removeItem("barte-refresh-token");
  signOut();
}

function ImpersonationBanner({ email }: { email?: string }) {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => {
    const exp = Number(localStorage.getItem("barte-impersonation-exp") || "0");
    return Math.max(0, exp - Math.floor(Date.now() / 1000));
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const exp = Number(localStorage.getItem("barte-impersonation-exp") || "0");
      const remaining = Math.max(0, exp - Math.floor(Date.now() / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        endImpersonation();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timeDisplay = `${minutes}:${String(seconds).padStart(2, "0")}`;
  const impersonator = localStorage.getItem("barte-impersonator") || "";

  return (
    <Box
      bg="blue.50"
      border="1px solid"
      borderColor="blue.200"
      borderRadius="md"
      p={4}
      minW="100%"
      maxW="916px"
      mx="auto"
      mb={5}
    >
      <Flex justify="space-between" align="center">
        <Box>
          <Text fontWeight="bold" color="blue.700">
            Sessao de impersonation ({timeDisplay})
          </Text>
          <Text fontSize="sm" color="blue.600">
            Visualizando como {email}. Sessao iniciada por {impersonator}.
          </Text>
        </Box>
        <CloseButton color="blue.700" onClick={() => endImpersonation()} />
      </Flex>
    </Box>
  );
}

const WithAuth = (WrappedComponent) => {
  function formatCurrentUrlToPathRoute(url, path) {
    let pathReturn = url;
    if (path.includes(":id")) {
      let urlToArray = url.split("/");
      urlToArray.pop();
      pathReturn = urlToArray.join("/") + "/:id";
    }
    return pathReturn;
  }

  return (props) => {
    const pathnameUrl = window.location.pathname;
    const roleSeller = JSON.parse(localStorage.getItem("barte-user-info"));
    const [updateTerms, setUpdateTerms] = useState<boolean>(false);
    const [logoff, setLogoff] = useState<boolean>(false);
    const [newTerms, setNewTerms] = useState<boolean>(false);
    const [typeSeller, setTypeSeller] = useState<string>("");
    const [featureTogglesUser, setFeatureTogglesUser] = useState<string | null>(localStorage.getItem("barte-features-list-user"));
    const featureToShow = flatRoutesList().find((item) => {
      return item.path === formatCurrentUrlToPathRoute(pathnameUrl, item.path);
    });
    const { currentTerms, isValidTerms } = useTerms();
    const { getFeatureToggles } = useFeatureToggle();
    const [isBlocked, setIsBlocked] = useState<boolean | null>(false)
    const navigate = useNavigate();

    const getSettingsItems = () => {
      const configRoute = RoutesList.find(route => route.title === "Configurações");
      if (!configRoute?.items) return [];

      const currentFeatures = featureTogglesUser ? JSON.parse(featureTogglesUser) : [];

      const exactMatchFeatureToggles = (item: any) =>
        item.isShowMenu &&
        isExactRole(roleSeller?.roles, item?.role) &&
        isExactFeatureToggle(currentFeatures, item?.feature);

      return configRoute.items
        .filter(exactMatchFeatureToggles)
        .map((item: any) => ({
          label: item.title,
          onClick: () => navigate(item.path),
        }));
    };

    const settingsItems = getSettingsItems();

    async function getFeatureTogglesList() {
      const previousFeatureToggles = localStorage.getItem("barte-features-list-user");
      try {
        const featureToggleResponse = await getFeatureToggles();
        const updatedFeatureToggles = [...featureToggleResponse.data];
        const currentUser = JSON.parse(localStorage.getItem("barte-user-info") || "null");
        const updatedBankingRoles = currentUser?.bankingRoles?.filter((br: string) => br !== "SELLER_ADMIN")
        if (updatedBankingRoles?.length > 0) {
          const index = updatedFeatureToggles.findIndex(feature => feature === "view_create_banking_account");
          if (index !== -1) {
            updatedFeatureToggles.splice(index, 1);
          }
        }
        if (featureToggleResponse.data.includes("disable_paymentlink")) {
          const index = updatedFeatureToggles.findIndex(feature => feature === "view_seller_payment_link");
          if (index !== -1) {
            updatedFeatureToggles.splice(index, 1);
          }
        }
        const nextFeatureToggles = JSON.stringify(updatedFeatureToggles);
        localStorage.setItem("barte-features-list-user", nextFeatureToggles);
        setFeatureTogglesUser(nextFeatureToggles);
      } catch (error) {
        console.log(error);
        if (previousFeatureToggles !== null) {
          localStorage.setItem("barte-features-list-user", previousFeatureToggles);
        }
        setFeatureTogglesUser(previousFeatureToggles);
      }
    }

    async function IsvalidTerms() {
      try {
        const response = await isValidTerms();
        response?.data === true ? setUpdateTerms(false) : setUpdateTerms(true)
        return response;
      } catch (error) {
        return error
      }
    }

    async function UploadTerms() {
      try {
        const response = await currentTerms();
        setNewTerms(true)
        return response;
      } catch (error) {
        return error
      }
    }

    useEffect(() => {
      getFeatureTogglesList();
    }, []);

    useEffect(() => {
      const roleSeller = JSON.parse(localStorage.getItem("barte-user-info"));
      if (roleSeller) {
        setTypeSeller(roleSeller?.roles)
      }
    }, [])

    useEffect(() => {
      IsvalidTerms();
    }, [newTerms]);

    useEffect(() => {
      if (logoff === true) {
        localStorage.removeItem("barte-features-list-user");
        localStorage.removeItem("barte-user-info");
        signOut();
        setLogoff(false);
        navigate('/login');
      }
    }, [logoff])

    return (
      <>
        {updateTerms && <AlertTerms setLogoff={setLogoff} typeSeller={typeSeller} UploadTerms={UploadTerms} />}
        <Box position="fixed" top={0} left={0} right={0} zIndex={100}>

          <Header variant="seller"
            pillsLabel={roleSeller?.companyName}
            avatarProps={{
              variant: 'icon',
              size: 'lg',
              color: 'inverse'
            }}
            logo={<img src={getTenantConfig().logoUrl} alt="Company Logo" />}
            avatarDrawerContent={
              <Tooltip label="Perfil" placement="bottom">
                <DrawerList>
                  <ActionDrawerItems
                    title={roleSeller?.email} items={[{
                      label: 'FAQ',
                      variant: 'icon',
                      onClick: () => window.open('https://www.barte.com/central-de-ajuda'),
                      icon: <Icon icon={QuestionIcon} />
                    }, {
                      label: 'Sign Out',
                      variant: 'icon',
                      onClick: () => setLogoff(true),
                      icon: <Icon icon={SignOutIcon} />
                    }]} />
                </DrawerList>
              </Tooltip>
            }>
            <Tooltip label="Notificações" placement="bottom">
              <Box>
                <NotificationDrawer />
              </Box>
            </Tooltip>
            {settingsItems.length > 0 && (
              <Tooltip label="Configurações" placement="bottom">
                <Box>
                  <Header.Action icon={GearSixIcon} ariaLabel="Settings"
                    drawerContent={
                      <DrawerList>
                        {settingsItems.map((item, index) => (
                          <ActionDrawerItems
                            key={`settings-item-${index}`}
                            items={[{
                              label: item.label,
                              variant: 'icon',
                              onClick: item.onClick,
                            }]}
                          />
                        ))}
                      </DrawerList>
                    }
                  />
                </Box>
              </Tooltip>
            )}
          </Header>
        </Box>
        <Grid
          height="100vh"
          gridTemplateColumns="auto 1fr"
          gridTemplateRows="1fr"
          gridTemplateAreas={`"S B"`}
          bgColor="#FFF"
          pt="64px"
        >
          <Box gridArea={"S"} bgColor="#FFF" zIndex={1}>
            <Sidebar />
          </Box>
          <Box p="20px 35px 50px" gridArea={"B"} h="100%" bgColor="#FFF" overflowY="auto">

            {
              localStorage.getItem("barte-impersonation") &&
              <ImpersonationBanner email={roleSeller?.email} />
            }

            {
              isBlocked === true &&
              <Box
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                borderRadius="md"
                p={4}
                minW="100%"
                maxW="916px"
                mx="auto"
                mb={5}
              >
                <Flex justify="space-between" align="center">
                  <Flex align="start" gap={3}>
                    <FiXCircle color="#D32F2F" />
                    <Box>
                      <Text fontWeight="bold" >
                        Seller com repasse bloqueado
                      </Text>
                      <Text fontSize="sm" >
                        Entre em contato com o suporte para atualizar seu cadastro.
                      </Text>
                    </Box>
                  </Flex>

                  <Flex align="center" gap={3}>
                    <CloseButton onClick={() => {
                      setIsBlocked(false)
                    }} />
                  </Flex>
                </Flex>
              </Box>
            }

            {/**@ts-ignore */}
            {(isExactRole(roleSeller?.roles, featureToShow?.role) && isExactFeatureToggle(featureTogglesUser, featureToShow?.feature)) ? (
              <WrappedComponent {...props} />
            ) : (
              <span>Você não tem permissão para visualizar essa página!</span>
            )}
          </Box>
        </Grid>
      </>

    );
  }
};

export { WithAuth };
